import { gql, useMutation, useQuery } from '@apollo/client';
import {
  Button,
  ButtonGroup,
  FormControl,
  FormErrorMessage,
  FormHelperText,
  FormLabel,
  Input,
  Modal,
  ModalBody,
  ModalContent,
  ModalDescription,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  ModalTitle,
  Textarea,
} from '@apollo/orbit';
import { useFormik } from 'formik';
import React from 'react';
import * as Yup from 'yup';

import { Form as FormWrapper } from 'src/components/common/form/Form';
import { Loading } from 'src/components/common/loading/Loading';
import { ErrorMessage } from 'src/components/errorMessage/ErrorMessage';
import {
  PQListMetaModalQuery,
  PQListMetaModalQueryVariables,
  PQListMetaUpdateModalMutation,
  PQListMetaUpdateModalMutationVariables,
} from 'src/lib/graphqlTypes/types';

interface Props {
  isOpen: boolean;
  graphId: string;
  listId: string;
  onClose: () => void;
  __test?: {
    mutationError?: Error;
    loading?: Boolean;
    submitting?: Boolean;
  };
}

const pqNameMinLength = 1;
const pqNameMaxLength = 128;
const pqDescriptionMaxLength = 4000;

const validationSchema = Yup.object().shape({
  name: Yup.string()
    .required('Please provide a name for this list.')
    .trim()
    .min(pqNameMinLength)
    .max(pqNameMaxLength, `Maximum ${pqNameMaxLength} characters`),
  description: Yup.string()
    .notRequired()
    .trim()
    .max(
      pqDescriptionMaxLength,
      `Maximum ${pqDescriptionMaxLength} characters`,
    ),
});

const pqListMetaModalQuery = gql<
  PQListMetaModalQuery,
  PQListMetaModalQueryVariables
>`
  query PQListMetaModalQuery($graphId: ID!, $listId: ID!) {
    graph(id: $graphId) {
      id
      persistedQueryList(id: $listId) {
        id
        name
        description
      }
    }
  }
`;

const pqListMetaUpdateModalMutation = gql<
  PQListMetaUpdateModalMutation,
  PQListMetaUpdateModalMutationVariables
>`
  mutation PQListMetaUpdateModalMutation(
    $graphId: ID!
    $listId: ID!
    $name: String!
    $description: String
  ) {
    service(id: $graphId) {
      persistedQueryList(id: $listId) {
        id
        updateMetadata(name: $name, description: $description) {
          ... on UpdatePersistedQueryListMetadataResult {
            persistedQueryList {
              id
              name
              description
            }
          }
          ... on PermissionError {
            message
          }
        }
      }
    }
  }
`;

export const PersistedQueryListMetaUpdateModal = ({
  isOpen,
  graphId,
  listId,
  onClose,
  __test,
}: Props) => {
  const {
    data: pqListData,
    loading: pqListLoading,
    error: pqListError,
  } = useQuery(pqListMetaModalQuery, {
    variables: { graphId, listId },
  });

  const [mutate, { error: mutationError }] = useMutation(
    pqListMetaUpdateModalMutation,
  );

  const {
    values,
    errors,
    touched,
    handleChange,
    setFieldTouched,
    setFieldError,
    handleSubmit,
    isSubmitting: formikSubmitting,
  } = useFormik<Yup.InferType<typeof validationSchema>>({
    initialValues: {
      name: pqListData?.graph?.persistedQueryList?.name ?? '',
      description: pqListData?.graph?.persistedQueryList?.description,
    },
    enableReinitialize: true,
    validationSchema,
    validateOnChange: false,
    validateOnBlur: false,
    async onSubmit(submitted) {
      const { name, description } = validationSchema.cast(submitted);
      const result = await mutate({
        variables: {
          graphId,
          listId,
          name,
          description,
        },
      });
      if (
        result.data?.service?.persistedQueryList.updateMetadata.__typename ===
        'UpdatePersistedQueryListMetadataResult'
      ) {
        onClose();
      }
    },
  });

  const loading = pqListLoading || Boolean(__test?.loading);
  const submitting = formikSubmitting || Boolean(__test?.submitting);

  return (
    <Modal isOpen={isOpen} size="xl" onClose={onClose}>
      <ModalOverlay />
      <FormWrapper onSubmit={handleSubmit} noValidate>
        <ModalContent>
          <ModalHeader>
            <ModalTitle>Update Persisted Query List</ModalTitle>
            <ModalDescription>
              Update name and description of the this list.
            </ModalDescription>
          </ModalHeader>
          <ModalBody>
            {loading ? (
              <Loading size="md" />
            ) : (
              <>
                <FormControl
                  isRequired
                  isInvalid={!!errors.name && !!touched.name}
                >
                  <FormLabel>Name</FormLabel>
                  <Input
                    name="name"
                    isDisabled={submitting || loading}
                    placeholder="e.g. internal, third-party, etc."
                    autoFocus
                    onChange={(event) => {
                      setFieldTouched('name');
                      setFieldError('name', undefined);
                      handleChange(event);
                    }}
                    value={values.name ?? ''}
                  />
                  <FormErrorMessage>{errors.name}</FormErrorMessage>
                </FormControl>
                <FormControl
                  isInvalid={!!errors.description && !!touched.description}
                >
                  <FormLabel>Description</FormLabel>
                  <Textarea
                    isDisabled={submitting || loading}
                    name="description"
                    id="description"
                    onChange={(event) => {
                      setFieldTouched('description');
                      handleChange(event);
                    }}
                    placeholder="Help your team understand how this list is used."
                    value={values.description ?? undefined}
                  />
                  {!touched.description ? (
                    <FormHelperText>&nbsp;</FormHelperText>
                  ) : errors.description ? (
                    <FormErrorMessage>{errors.description}</FormErrorMessage>
                  ) : (
                    <FormHelperText>
                      {pqDescriptionMaxLength -
                        (values.description?.length ?? 0)}{' '}
                      characters remaining
                    </FormHelperText>
                  )}
                </FormControl>
              </>
            )}
            {(pqListError || mutationError || __test?.mutationError) && (
              <ErrorMessage>
                Something went wrong. Please refresh and try again.
              </ErrorMessage>
            )}
          </ModalBody>
          <ModalFooter>
            <ButtonGroup>
              <Button
                variant="secondary"
                type="button"
                isDisabled={submitting}
                onClick={onClose}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                isDisabled={loading}
                isLoading={submitting}
                loadingText="Save"
                type="submit"
              >
                Save
              </Button>
            </ButtonGroup>
          </ModalFooter>
        </ModalContent>
      </FormWrapper>
    </Modal>
  );
};
